import {
  WEEKDAYS,
  describeRecurrence,
  formatTimeOfDay,
  nextOccurrence,
  validateRecurrence,
  weekdayName,
  type CheckInTemplate,
  type Recurrence,
  type RecurrenceKind,
  type ReminderSchedule,
  type TimeOfDay,
  type ValidationError,
  type Weekday,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { Page, SubHeader } from '../../components/Layout.js'
import { Select, TextField } from '../../components/Field.js'
import { useToast } from '../../components/Toast.js'
import { formatTime, formatUpcomingDay } from '../../format/dates.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Reminders.module.css'

const KINDS: readonly { readonly kind: RecurrenceKind; readonly label: string }[] = [
  { kind: 'daily', label: 'Every day' },
  { kind: 'weekly', label: 'Certain days' },
  { kind: 'interval', label: 'Through the day' },
]

function parseTime(value: string): TimeOfDay | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value)
  if (match === null) {
    return null
  }
  return { hour: Number(match[1]), minute: Number(match[2]) }
}

/**
 * Creating and editing one reminder (T-702).
 *
 * The preview at the bottom is the point of the screen, not decoration: it is
 * the only way to check a recurrence without waiting a day for it, and it runs
 * the same `nextOccurrence` the list uses so the two cannot disagree.
 */
export function ReminderEditorScreen() {
  const { repos, clock } = useServices()
  const navigate = useNavigate()
  const toast = useToast()
  const params = useParams()
  const id = params['id']
  const isNew = id === undefined || id === 'new'

  const [loaded, setLoaded] = useState(isNew)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<RecurrenceKind>('daily')
  const [times, setTimes] = useState<readonly TimeOfDay[]>([{ hour: 8, minute: 0 }])
  const [weekdays, setWeekdays] = useState<readonly Weekday[]>([1, 2, 3, 4, 5])
  const [everyHours, setEveryHours] = useState(4)
  const [from, setFrom] = useState<TimeOfDay>({ hour: 8, minute: 0 })
  const [to, setTo] = useState<TimeOfDay>({ hour: 20, minute: 0 })
  const [templateId, setTemplateId] = useState('')
  const [templates, setTemplates] = useState<CheckInTemplate[]>([])
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setTemplates(await repos.templates.list())
    if (isNew) {
      return
    }
    const found = await repos.reminders.get(id)
    if (found !== undefined) {
      setName(found.name)
      setKind(found.recurrence.kind)
      setTemplateId(found.templateId ?? '')
      if (found.recurrence.kind === 'interval') {
        setEveryHours(found.recurrence.everyHours)
        setFrom(found.recurrence.from)
        setTo(found.recurrence.to)
      } else {
        setTimes(found.recurrence.times)
        if (found.recurrence.kind === 'weekly') {
          setWeekdays(found.recurrence.weekdays)
        }
      }
    }
    setLoaded(true)
  }, [repos, id, isNew])

  useEffect(() => {
    void load()
  }, [load])

  const recurrence: Recurrence =
    kind === 'daily'
      ? { kind: 'daily', times }
      : kind === 'weekly'
        ? { kind: 'weekly', weekdays, times }
        : { kind: 'interval', everyHours, from, to }

  const save = async () => {
    setSaving(true)
    try {
      const result = isNew
        ? await repos.reminders.create({
            name,
            recurrence,
            ...(templateId === '' ? {} : { templateId }),
          })
        : await repos.reminders.update(id, {
            name,
            recurrence,
            // Explicitly `undefined` clears the link rather than leaving the
            // old one in place when the user picks "no check-in".
            templateId: templateId === '' ? undefined : templateId,
          })

      if (!result.ok) {
        setErrors(result.errors)
        return
      }
      toast.show({ message: isNew ? 'Reminder added' : 'Reminder saved' })
      void navigate('/reminders')
    } finally {
      setSaving(false)
    }
  }

  const errorFor = (field: string) =>
    errors.find((error) => error.field === field)?.message

  const valid = validateRecurrence(recurrence).ok && name.trim() !== ''

  /*
   * The same computation the list runs, against a schedule that has not been
   * saved. Previewing with a second implementation is how a preview ends up
   * promising something the stored schedule will not do.
   */
  const now = clock.now()
  const preview: ReminderSchedule = {
    id: 'preview',
    name: name === '' ? 'Reminder' : name,
    enabled: true,
    recurrence,
    createdAt: now,
    updatedAt: now,
  }
  const upcoming = valid ? nextOccurrence(preview, now, clock.timeZone()) : null

  if (!loaded) {
    return (
      <Page>
        <SubHeader back={{ to: '/reminders', label: 'Reminders' }} />
        <h1>Reminder</h1>
        <p>Loading…</p>
      </Page>
    )
  }

  return (
    <Page>
      <SubHeader back={{ to: '/reminders', label: 'Reminders' }} />
      <h1>{isNew ? 'New reminder' : 'Reminder'}</h1>

      {/*
        A column with its own rhythm, like every other editor.

        Without it these fields were siblings of the heading with nothing
        between them: labels sat flush against the control above, the fieldset
        touched the select, and the whole screen read as one run-on block.
      */}
      <div className={styles['form']}>
        <TextField
          label="Name"
          value={name}
          {...(errorFor('name') === undefined ? {} : { error: errorFor('name') })}
          onChange={(event) => {
            setName(event.target.value)
          }}
        />

        <Select
          label="When"
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as RecurrenceKind)
          }}
        >
          {KINDS.map((option) => (
            <option key={option.kind} value={option.kind}>
              {option.label}
            </option>
          ))}
        </Select>

        {kind === 'weekly' && (
          <fieldset className={styles['group']}>
            <legend className={styles['legend']}>Days</legend>
            <div className={styles['days']}>
              {WEEKDAYS.map((day) => {
                const chosen = weekdays.includes(day)
                return (
                  <label key={day} className={styles['day']}>
                    <input
                      type="checkbox"
                      checked={chosen}
                      onChange={() => {
                        setWeekdays(
                          chosen
                            ? weekdays.filter((other) => other !== day)
                            : [...weekdays, day],
                        )
                      }}
                    />
                    {weekdayName(day)}
                  </label>
                )
              })}
            </div>
          </fieldset>
        )}

        {kind === 'interval' ? (
          <div className={styles['window']}>
            <TextField
              label="From"
              type="time"
              value={formatTimeOfDay(from)}
              onChange={(event) => {
                const parsed = parseTime(event.target.value)
                if (parsed !== null) {
                  setFrom(parsed)
                }
              }}
            />
            <TextField
              label="Until"
              type="time"
              value={formatTimeOfDay(to)}
              onChange={(event) => {
                const parsed = parseTime(event.target.value)
                if (parsed !== null) {
                  setTo(parsed)
                }
              }}
            />
            <Select
              label="Every"
              value={String(everyHours)}
              onChange={(event) => {
                setEveryHours(Number(event.target.value))
              }}
            >
              {[1, 2, 3, 4, 6, 8, 12].map((hours) => (
                <option key={hours} value={hours}>
                  {hours === 1 ? '1 hour' : `${String(hours)} hours`}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <fieldset className={styles['group']}>
            <legend className={styles['legend']}>Times</legend>
            {times.map((time, index) => (
              <div key={index} className={styles['time']}>
                <TextField
                  label={`Time ${String(index + 1)}`}
                  type="time"
                  value={formatTimeOfDay(time)}
                  onChange={(event) => {
                    const parsed = parseTime(event.target.value)
                    if (parsed !== null) {
                      setTimes(times.map((one, at) => (at === index ? parsed : one)))
                    }
                  }}
                />
                {times.length > 1 && (
                  <Button
                    variant="ghost"
                    aria-label={`Remove time ${String(index + 1)}`}
                    onClick={() => {
                      setTimes(times.filter((_, at) => at !== index))
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button
              onClick={() => {
                setTimes([...times, { hour: 20, minute: 0 }])
              }}
            >
              Add another time
            </Button>
          </fieldset>
        )}

        <Select
          label="Check-in"
          optional
          hint="Which check-in this reminder is for, if it is for one."
          value={templateId}
          onChange={(event) => {
            setTemplateId(event.target.value)
          }}
        >
          <option value="">No check-in</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name}
            </option>
          ))}
        </Select>

        {/*
        Verifiable without waiting a day for it. Both lines come from core —
        the sentence from `describeRecurrence`, the instant from
        `nextOccurrence` — so what this promises is what gets stored.
      */}
        <div className={styles['preview']} data-testid="preview">
          <p className={styles['previewLine']}>{describeRecurrence(recurrence)}</p>
          <p className={styles['previewNext']}>
            {upcoming === null
              ? 'Not a valid schedule yet.'
              : `First one would be ${formatUpcomingDay(upcoming, now)} at ${formatTime(upcoming)}.`}
          </p>
        </div>

        {errors.length > 0 && (
          <ul className={styles['errors']} data-testid="errors">
            {errors.map((error) => (
              <li key={`${error.code}:${error.message}`}>{error.message}</li>
            ))}
          </ul>
        )}

        <div className={styles['footer']}>
          <Button
            variant="primary"
            disabled={!valid || saving}
            onClick={() => void save()}
          >
            {isNew ? 'Create reminder' : 'Save reminder'}
          </Button>
          <Button variant="ghost" onClick={() => void navigate('/reminders')}>
            Cancel
          </Button>
        </div>
      </div>
    </Page>
  )
}
